import React from "react";
import { ClientApartment } from "../../../utils/api/apartmentRentalsAPI";
import { fmtAmount, fmtPrice, calendarNightPrice, effectiveNightPrice, CalDate } from "./bookingPricing";

export interface DayMap {
  [day: number]: "free" | "confirmed" | "pending" | "event" | "blocked" | "closed";
}

export interface CalendarLabels {
  free: string; occupied: string; event: string; discount: string;
  specialPrice: string; selected: string; notAvailable: string;
  notAvailableEvent: string; notAvailableBooking: string;
  pastDate: string;
}

interface CalendarProps {
  year: number;
  month: number;
  dayMap: DayMap;
  apt: ClientApartment;
  checkIn: CalDate | null;
  checkOut: CalDate | null;
  onSelectDay: (y: number, m: number, d: number) => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  cutoff: Date | null;
  monthNames: string[];
  dowLabels: string[];
  labels: CalendarLabels;
}

const Calendar: React.FC<CalendarProps> = ({
  year, month, dayMap, apt, checkIn, checkOut, onSelectDay, onPrev, onNext, canGoPrev, canGoNext, cutoff,
  monthNames, dowLabels, labels,
}) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Mon=0
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // checkOut is the departure date (day after the last night stayed), so the calendar highlights
  // checkIn through "checkOut minus one day" — the actual nights — and leaves the departure day
  // itself unmarked, since the guest doesn't occupy that night.
  const lastNightMs = checkOut
    ? new Date(checkOut.year, checkOut.month - 1, checkOut.day).getTime() - 86400000
    : null;

  const isSelected = (d: number) => {
    if (checkIn?.year === year && checkIn?.month === month && checkIn?.day === d) return true;
    if (lastNightMs == null) return false;
    const dayMs = new Date(year, month - 1, d).getTime();
    return dayMs === lastNightMs;
  };

  const isInRange = (d: number) => {
    if (!checkIn || lastNightMs == null) return false;
    const ciMs = new Date(checkIn.year, checkIn.month - 1, checkIn.day).getTime();
    const dayMs = new Date(year, month - 1, d).getTime();
    return dayMs > ciMs && dayMs < lastNightMs;
  };

  const isPast = (d: number) => new Date(year, month - 1, d) < today;

  const isClosed = (d: number) => {
    if (!cutoff) return false;
    const dayMs = new Date(year, month - 1, d).getTime();
    const cutMs = new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate()).getTime();
    return dayMs > cutMs;
  };

  const cells: React.ReactNode[] = dowLabels.map((dow, i) => (
    <div key={`dow-${i}`} className="pab-cal-dow">{dow}</div>
  ));

  for (let i = 0; i < firstDow; i++) {
    const prevDays = new Date(year, month - 1, 0).getDate();
    cells.push(
      <div key={`prev-${i}`} className="pab-cal-day pab-cal-day--muted">
        <span className="pab-cal-day-num">{prevDays - firstDow + i + 1}</span>
      </div>
    );
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const status = dayMap[d] || "free";
    const past = isPast(d);
    const closed = !past && isClosed(d);
    const booked = status === "confirmed" || status === "pending" || status === "blocked";
    const isEvent = status === "event";
    const selected = isSelected(d);
    const inRange = isInRange(d);
    const isToday =
      today.getFullYear() === year &&
      today.getMonth() + 1 === month &&
      today.getDate() === d;

    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const nightPrice = calendarNightPrice(apt, dateStr);
    const priceWithoutTiming = effectiveNightPrice(apt, dateStr);
    const isTimingApplied = Math.round(nightPrice) !== Math.round(priceWithoutTiming);
    const originalDisplayPrice = isTimingApplied ? priceWithoutTiming : apt.pricePerNight;
    const isSpecial = Math.round(nightPrice) !== Math.round(apt.pricePerNight);
    const isDiscount = nightPrice < apt.pricePerNight;
    // Every clickable day — checkin or a later night — must actually be free: it's a night
    // the guest stays, not just a point marker, so a booked day is unavailable in either phase.
    const unavailable = past || closed || isEvent || booked;

    const cls = [
      "pab-cal-day",
      isToday && "pab-cal-day--today",
      selected && "pab-cal-day--selected",
      !selected && inRange && "pab-cal-day--in-range",
      !selected && !inRange && booked && (status === "pending" ? "pab-cal-day--pending" : "pab-cal-day--confirmed"),
      !selected && !inRange && status === "blocked" && "pab-cal-day--blocked",
      !selected && !inRange && isEvent && "pab-cal-day--event",
      !selected && !inRange && closed && "pab-cal-day--closed",
      !selected && !inRange && !booked && !isEvent && !closed && !past && "pab-cal-day--free",
      !selected && !inRange && !booked && !isEvent && !closed && past && "pab-cal-day--past",
      !selected && !inRange && !booked && !isEvent && !closed && !past && isSpecial && "pab-cal-day--special",
    ]
      .filter(Boolean)
      .join(" ");

    const tooltipText = isEvent
      ? labels.notAvailableEvent
      : closed
      ? labels.notAvailableBooking
      : status === "blocked"
      ? labels.notAvailable
      : booked
      ? labels.occupied
      : past
      ? labels.pastDate
      : isDiscount
      ? `${fmtAmount(nightPrice, apt.currency)}/noć · ${labels.discount.toLowerCase()} (bila ${fmtAmount(originalDisplayPrice, apt.currency)})`
      : isSpecial
      ? `${fmtAmount(nightPrice, apt.currency)}/noć · ${labels.specialPrice.toLowerCase()} (osnovna ${fmtAmount(apt.pricePerNight, apt.currency)})`
      : `${fmtAmount(nightPrice, apt.currency)}/noć`;

    cells.push(
      <div
        key={`d-${d}`}
        className={cls}
        onClick={() => { if (!unavailable) onSelectDay(year, month, d); }}
        title={tooltipText}
      >
        <span className="pab-cal-day-num">{d}</span>
        {!unavailable && (
          <>
            {(isDiscount || isSpecial) && (
              <span className="pab-cal-day-price pab-cal-day-price--original">
                {fmtPrice(originalDisplayPrice, apt.currency)}
              </span>
            )}
            <span className={`pab-cal-day-price${isDiscount ? " pab-cal-day-price--ok" : isSpecial ? " pab-cal-day-price--special" : ""}`}>
              {fmtPrice(nightPrice, apt.currency)}
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="pab-cal">
      <div className="pab-cal-nav">
        <button type="button" className="pab-cal-step" onClick={onPrev} disabled={!canGoPrev} aria-label="Prethodni mjesec">‹</button>
        <span className="pab-cal-month-label">{monthNames[month - 1]} {year}</span>
        <button type="button" className="pab-cal-step" onClick={onNext} disabled={!canGoNext} aria-label="Sljedeći mjesec">›</button>
      </div>

      <div className="pab-cal-legend">
        <span><i style={{ background: "var(--pa-panel)", border: "1px solid var(--pa-line)" }} /> {labels.free}</span>
        <span><i style={{ background: "var(--pa-rose-soft)" }} /> {labels.occupied}</span>
        <span><i style={{ background: "var(--pa-warn-soft)" }} /> {labels.discount}</span>
        <span><i style={{ background: "var(--pa-accent)" }} /> {labels.selected}</span>
      </div>

      <div className="pab-cal-grid">{cells}</div>
    </div>
  );
};

export default Calendar;
